---
title: "Quick start: your first loan in five minutes"
slug: "quick-start-first-loan"
category: "getting-started"
description: "A tiny end-to-end tour: add a borrower, catalogue a book, and run a real loan."
tags: ["quick start", "first loan", "tutorial", "getting started"]
order: 3
---

This is the fastest way to prove BookBurrow works: add one pupil, catalogue one book, and lend it out. You can do it without a barcode scanner if you don't have one yet.

By the end of this guide you'll have:

- One borrower in the system.
- One book in your catalogue.
- One active loan linking them together.

Real libraries obviously have thousands of each, but the workflow is exactly the same.

## Step 1 — Add a borrower

<Steps>
  <Step title="Open the Borrowers tab">
    It's the person-shaped icon in the left sidebar.
  </Step>
  <Step title="Click Add borrower">
    Fill in the pupil's name, year group, and (optionally) class. Everything else is optional and can be filled in later.
  </Step>
  <Step title="Give the borrower a card number">
    Either type any number you like (e.g. `1001`) or scan a blank barcode label. This number is what links the pupil's card to their record.
  </Step>
  <Step title="Click Save">
    The new borrower appears in the list.
  </Step>
</Steps>

## Step 2 — Catalogue a book

<Steps>
  <Step title="Open the Books tab">
    The book icon in the sidebar.
  </Step>
  <Step title="Click Add book">
    In the **Barcode or ISBN** field, either type an ISBN (grab one from the back of any book) or scan the barcode on a real book. BookBurrow looks it up online and fills in the title, author, publisher and page count automatically.
  </Step>
  <Step title="Adjust if needed">
    If the ISBN lookup doesn't find a match — for a very old book, say — you can always type the details in by hand.
  </Step>
  <Step title="Click Save">
    The book appears in your catalogue, marked Available.
  </Step>
</Steps>

<Callout variant="tip" title="No scanner? No problem">
BookBurrow works completely fine with a keyboard only. Every field that accepts a scan also accepts typed input — the scanner is just a faster way to enter numbers. You can buy one later without losing any work.
</Callout>

## Step 3 — Lend the book

<Steps>
  <Step title="Open the Lend tab">
    This is the main day-to-day screen: one big focused input, one message area.
  </Step>
  <Step title="Scan or type the borrower's card number">
    BookBurrow shows their name, year group, and anything they currently have on loan.
  </Step>
  <Step title="Scan or type the book's barcode">
    The screen instantly updates: the book is now on loan to that pupil, the borrower's active loans count goes up, and the book's status changes to On loan.
  </Step>
</Steps>

That's a complete loan transaction, start to finish. In a real library, a helper might do twenty of these in two minutes. When the pupil brings the book back, the **Return** tab works exactly the same way — scan the book and it's returned.

## What next?

You've now done a full loan cycle with one pupil and one book. The obvious next step is to scale up:

- **[Import all your borrowers](/help/guides/importing-from-sims-arbor-bromcom)** from your school's MIS in one pass instead of adding them one at a time.
- **[Learn how ISBN scanning works](/help/guides/scanning-books-isbn-lookup)** so you can catalogue hundreds of books quickly.
- **[Set up automatic backups](/help/guides/configuring-automatic-backups)** before you start trusting real data to it — this is the one thing you really should do today.
